import React, { useState } from 'react';
import { View, TextInput, StyleSheet } from 'react-native';
import { Card } from '@ant-design/react-native';
import Button from '@md-design-system/Button/Button';
import { Text } from '@shared/components/text';
import { useAuth } from 'app/auth/hooks/useAuth';
import { ActivityName } from 'app/_modules/core/types';
import { checkMeasurementAlert, alertTypeResult } from 'app/_modules/core/utils';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { colorPalette } from '@shared/config/colorsConfig';

type TResult = { title: string; description: string; type: string; alertType: string } | null;

const TESTABLE_PARAMS: { name: ActivityName; label: string; fields: { key: string; placeholder: string }[] }[] = [
    { name: ActivityName.BLOOD_PRESSURE, label: 'BP', fields: [{ key: 'systolic', placeholder: 'Sistolica' }, { key: 'diastolic', placeholder: 'Diastolica' }] },
    { name: ActivityName.HEART_RATE, label: 'HR', fields: [{ key: 'heart_rate', placeholder: 'BPM' }] },
    { name: ActivityName.WEIGHT, label: 'Peso', fields: [{ key: 'weight', placeholder: 'Kg' }] },
    { name: ActivityName.SPO2, label: 'SpO2', fields: [{ key: 'saturation', placeholder: '%' }] },
    { name: ActivityName.TEMPERATURE, label: 'Temp', fields: [{ key: 'temperature', placeholder: '°C' }] },
    { name: ActivityName.DYSPNEA, label: 'Dispnea', fields: [{ key: 'dyspnea', placeholder: 'Score' }] },
];

const ALERT_COLORS: Record<string, string> = {
    green: colorPalette.success[700],
    yellow: colorPalette.warning[500],
    red: colorPalette.error[500],
};

export const AlertLogicTester: React.FC = () => {
    const { meData } = useAuth();
    const { t } = dynamicTranslations();
    const [selectedParam, setSelectedParam] = useState<ActivityName>(ActivityName.BLOOD_PRESSURE);
    const [values, setValues] = useState<Record<string, string>>({});
    const [result, setResult] = useState<TResult>(null);

    const currentParam = TESTABLE_PARAMS.find(p => p.name === selectedParam)!;

    const handleCheck = () => {
        const measurement = Object.fromEntries(
            currentParam.fields.map(f => [f.key, parseFloat(values[f.key] || '0')])
        );

        const vitalParameterData = meData?.current_pathway?.vital_parameters?.find(
            vp => vp.vital_parameter.name === selectedParam
        );

        const monitoringParams = vitalParameterData?.vital_parameter?.monitoring_parameters ?? [];

        console.log('[AlertLogicTester] measurement:', measurement);
        console.log('[AlertLogicTester] monitoring_parameters:', JSON.stringify(monitoringParams, null, 2));

        const { alertType, thresholdType } = monitoringParams.length
            ? checkMeasurementAlert(measurement, monitoringParams)
            : { alertType: 'green' as const, thresholdType: undefined };

        console.log('[AlertLogicTester] result → alertType:', alertType, '| thresholdType:', thresholdType);

        const { title, description, type } = alertTypeResult(selectedParam, alertType, thresholdType);

        console.log('[AlertLogicTester] display → type:', type, '| title:', title, '| description:', description);

        setResult({ title, description, type, alertType });
    };

    return (
        <Card style={{ margin: 16 }}>
            <Card.Header title="Alert Logic Tester" style={{ backgroundColor: '#f5f5f5' }} />
            <Card.Body>
                <View style={styles.container}>
                    <View style={styles.paramRow}>
                        {TESTABLE_PARAMS.map(p => (
                            <Button
                                key={p.name}
                                variant={selectedParam === p.name ? 'primary' : 'outline'}
                                size="small"
                                onPress={() => { setSelectedParam(p.name); setValues({}); setResult(null); }}
                                style={styles.paramBtn}
                            >
                                {p.label}
                            </Button>
                        ))}
                    </View>

                    {currentParam.fields.map(f => (
                        <TextInput
                            key={f.key}
                            style={styles.input}
                            placeholder={f.placeholder}
                            keyboardType="numeric"
                            value={values[f.key] || ''}
                            onChangeText={v => setValues(prev => ({ ...prev, [f.key]: v }))}
                        />
                    ))}

                    <Button variant="secondary" onPress={handleCheck}>
                        Verifica
                    </Button>

                    {result && (
                        <View style={[styles.result, { borderColor: ALERT_COLORS[result.alertType] }]}>
                            <Text style={{ color: ALERT_COLORS[result.alertType], fontWeight: '600' }}>
                                {t(result.type)} {t(result.title)}
                            </Text>
                            <Text style={styles.description}>{t(result.description)}</Text>
                            <Text style={[styles.badge, { color: ALERT_COLORS[result.alertType] }]}>
                                {result.alertType.toUpperCase()}
                            </Text>
                        </View>
                    )}
                </View>
            </Card.Body>
        </Card>
    );
};

const styles = StyleSheet.create({
    container: { gap: 12, padding: 8 },
    paramRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    paramBtn: { marginVertical: 0 },
    input: {
        borderWidth: 1,
        borderColor: colorPalette.gray[300],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        fontSize: 16,
    },
    result: {
        borderWidth: 2,
        borderRadius: 8,
        padding: 12,
        gap: 4,
    },
    description: { fontSize: 13, color: colorPalette.gray[500] },
    badge: { fontSize: 11, fontWeight: '700', marginTop: 4 },
});
